import { useState, useRouter } from "#imports";
import { onBeforeUnmount } from "vue";

export function useHistoryNavigation() {
  // Use useState for SSR-safe state isolation (not module-level reactive)
  const historyState = useState("historyState", () => ({
    isBackNavigation: false,
    previousRoute: null as string | null,
    currentRoute: null as string | null,
  }));

  const router = useRouter();

  let cleanupFns: Array<() => void> = [];

  // Track navigation direction
  const trackNavigation = () => {
    if (typeof window === "undefined") return;

    // Store the current path when navigating
    const storeCurrentPath = () => {
      historyState.value.previousRoute = historyState.value.currentRoute;
      historyState.value.currentRoute =
        window.location.pathname + window.location.search;
    };

    // On initial page load
    storeCurrentPath();

    // Track navigation changes — store cleanup handles
    const unsubBeforeEach = router.beforeEach((to, from) => {
      historyState.value.isBackNavigation = false;
      return true;
    });

    const unsubAfterEach = router.afterEach((to, from) => {
      storeCurrentPath();
    });

    // Listen to popstate for back/forward button clicks
    const handlePopstate = () => {
      historyState.value.isBackNavigation = true;
    };
    window.addEventListener("popstate", handlePopstate);

    cleanupFns.push(
      unsubBeforeEach,
      unsubAfterEach,
      () => window.removeEventListener("popstate", handlePopstate),
    );
  };

  // Set up function to provide the navigation state globally
  const provideHistoryNavigation = () => {
    if (typeof window !== "undefined") {
      trackNavigation();
    }

    // Cleanup on unmount
    onBeforeUnmount(() => {
      cleanupFns.forEach((fn) => fn());
      cleanupFns = [];
    });

    return { historyState };
  };

  // Function to use the history state in components
  const useHistoryState = () => {
    return {
      isBackNavigation: historyState.value.isBackNavigation,
      historyState: historyState.value,
    };
  };

  return {
    historyState: historyState.value,
    provideHistoryNavigation,
    useHistoryState,
  };
}
